import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Download, MessageCircle, Send, Lock, Unlock, History, Clock, Upload, Eye, EyeOff } from 'lucide-react';
import AdminLayout from './AdminLayout';
import { api, API } from '../../lib/api';
import { statusLabel, statusTone, bytesToHuman, fmtDateTime, STATUS_LABELS, downloadFromLink } from '../../lib/docs';
import { toast } from 'sonner';
import PdfViewer from '../../components/PdfViewer';

const STATUS_OPTIONS = Object.entries(STATUS_LABELS);

export default function AdminDocumentDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newStatus, setNewStatus] = useState('');
  const [statusMessage, setStatusMessage] = useState('');
  const [noteBody, setNoteBody] = useState('');
  const [internalOnly, setInternalOnly] = useState(true);
  const [categories, setCategories] = useState([]);
  const [meta, setMeta] = useState({ category_id: '', tax_year: '' });
  const [viewLink, setViewLink] = useState(null);
  const versionRef = useRef();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [d, cats] = await Promise.all([
        api.specialistDocument(id),
        api.documentCategories(),
      ]);
      setDoc(d);
      setCategories(cats);
      setNewStatus(d.status);
      setMeta({ category_id: d.category_id, tax_year: d.tax_year });
      if (/pdf/i.test(d.mime || '')) {
        try { setViewLink(await api.specialistDocumentViewLink(id)); } catch { /* fallback */ }
      } else { setViewLink(null); }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load document');
      nav('/admin/documents');
    } finally { setLoading(false); }
  }, [id, nav]);
  useEffect(() => { load(); }, [load]);

  const download = async () => {
    try {
      const { url } = await api.specialistDocumentDownloadLink(id);
      downloadFromLink(url, API.replace(/\/api$/, ''));
    } catch { toast.error('Could not generate download link'); }
  };

  const saveStatus = async () => {
    try {
      const updated = await api.specialistDocStatus(id, { status: newStatus, message: statusMessage });
      setDoc(updated); setStatusMessage('');
      toast.success('Status updated');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not update'); }
  };

  const saveMeta = async () => {
    try {
      const updated = await api.specialistDocMetadata(id, {
        category_id: meta.category_id || null,
        tax_year: meta.tax_year ? parseInt(meta.tax_year, 10) : null,
      });
      setDoc(updated);
      toast.success('Reclassified');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not reclassify'); }
  };

  const addNote = async () => {
    if (!noteBody.trim()) return;
    try {
      await api.specialistAddNote(id, noteBody.trim(), internalOnly);
      setNoteBody('');
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not save note'); }
  };

  const uploadVersion = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append('file', file);
    // Re-use the member endpoint with member context — specialists upload via /specialist/members/{id}/documents instead,
    // but versioning on existing docs uses the member endpoint which only the member is authorized for; for now keep version replace member-side.
    toast.error('Specialists: please ask the member to upload a new version, or upload a fresh document.');
  };

  if (loading || !doc) return (<AdminLayout title="Document"><div className="py-20 text-center text-neutral-500">Loading…</div></AdminLayout>);

  return (
    <AdminLayout title={doc.original_name} subtitle={`${doc.member?.name || 'Member'} · ${doc.category_name} · ${doc.tax_year}`} actions={
      <Link to={doc.member ? `/admin/members/${doc.member.id}/tax-file` : '/admin/documents'} className="inline-flex items-center gap-1 text-[12.5px] text-neutral-500 hover:text-[#c89200]">
        <ArrowLeft className="w-3.5 h-3.5" /> Back
      </Link>
    }>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {viewLink && (
            <PdfViewer viewUrl={viewLink.url}
                       fileName={doc.original_name}
                       mime={doc.mime}
                       documentId={doc.id}
                       canDownload
                       canPrint
                       className="min-h-[640px]" />
          )}
          <div className="bg-white rounded-xl border border-neutral-200 p-5">
            <div className="flex flex-wrap items-center gap-3 justify-between">
              <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-[12px] font-semibold ${statusTone(doc.status)}`}>{statusLabel(doc.status)}</span>
              <div className="flex gap-2">
                <button onClick={download} data-testid="admin-download" className="inline-flex items-center gap-2 bg-[#f5b300] hover:bg-[#e0a200] text-[#14110f] font-semibold px-3.5 py-2 rounded-md text-[12.5px]">
                  <Download className="w-3.5 h-3.5" /> Download
                </button>
              </div>
            </div>
            <dl className="mt-5 grid grid-cols-2 gap-x-6 gap-y-3 text-[13px]">
              <div><dt className="text-[11px] uppercase tracking-wider text-neutral-500">Uploaded</dt><dd>{fmtDateTime(doc.uploaded_at)} by {doc.uploaded_by_name} <span className="text-neutral-400">({doc.uploaded_by_role})</span></dd></div>
              <div><dt className="text-[11px] uppercase tracking-wider text-neutral-500">Type · Size</dt><dd>{doc.mime} · {bytesToHuman(doc.size)}</dd></div>
              <div><dt className="text-[11px] uppercase tracking-wider text-neutral-500">Storage</dt><dd>{doc.storage_provider} · sync {doc.sync_status}</dd></div>
              <div><dt className="text-[11px] uppercase tracking-wider text-neutral-500">Version</dt><dd>v{doc.version}</dd></div>
            </dl>
            {doc.description && <p className="mt-4 p-3 rounded bg-neutral-50 text-[13px] text-neutral-700">{doc.description}</p>}
          </div>

          {/* Status change */}
          <div className="bg-white rounded-xl border border-neutral-200 p-5">
            <h3 className="text-[15px] font-bold text-[#14110f] mb-3">Update status</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <select data-testid="status-select" value={newStatus} onChange={(e) => setNewStatus(e.target.value)}
                      className="px-3 py-2.5 rounded-md border border-neutral-200 text-[13.5px] focus:border-[#f5b300] outline-none">
                {STATUS_OPTIONS.map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
              <input data-testid="status-message" value={statusMessage} onChange={(e) => setStatusMessage(e.target.value)}
                     placeholder="Optional message"
                     className="md:col-span-1 px-3 py-2.5 rounded-md border border-neutral-200 text-[13.5px] focus:border-[#f5b300] outline-none" />
              <button data-testid="status-save" onClick={saveStatus} className="bg-[#f5b300] hover:bg-[#e0a200] text-[#14110f] font-semibold rounded-md text-[13px] px-4">
                Apply status
              </button>
            </div>
          </div>

          {/* Reclassify */}
          <div className="bg-white rounded-xl border border-neutral-200 p-5">
            <h3 className="text-[15px] font-bold text-[#14110f] mb-3">Reclassify</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <select data-testid="meta-category" value={meta.category_id} onChange={(e) => setMeta({ ...meta, category_id: e.target.value })}
                      className="px-3 py-2.5 rounded-md border border-neutral-200 text-[13.5px] focus:border-[#f5b300] outline-none">
                {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              <input data-testid="meta-year" type="number" value={meta.tax_year} onChange={(e) => setMeta({ ...meta, tax_year: e.target.value })}
                     className="px-3 py-2.5 rounded-md border border-neutral-200 text-[13.5px] focus:border-[#f5b300] outline-none" />
              <button data-testid="meta-save" onClick={saveMeta} className="bg-[#14110f] hover:bg-[#291d12] text-white font-semibold rounded-md text-[13px] px-4">
                Save metadata
              </button>
            </div>
          </div>

          {/* Notes */}
          <div className="bg-white rounded-xl border border-neutral-200 p-5">
            <h3 className="text-[15px] font-bold text-[#14110f] mb-3 flex items-center gap-2"><MessageCircle className="w-4 h-4 text-[#c89200]" /> Notes</h3>
            <ul className="space-y-3" data-testid="admin-notes-list">
              {(doc.notes || []).length === 0 ? <p className="text-[13px] text-neutral-500">No notes yet.</p>
                : doc.notes.map((n) => (
                  <li key={n.id} className={`rounded-lg p-3 ${n.internal_only ? 'bg-amber-50 border border-amber-100' : 'bg-neutral-50 border border-neutral-100'}`}>
                    <p className="text-[11.5px] text-neutral-500 mb-1 flex items-center gap-2">
                      <span className="font-semibold text-[#14110f]">{n.author_name}</span>
                      <span>{fmtDateTime(n.created_at)}</span>
                      {n.internal_only ? <span className="inline-flex items-center gap-1 text-amber-700 font-semibold"><EyeOff className="w-3 h-3" /> Internal</span>
                        : <span className="inline-flex items-center gap-1 text-emerald-700 font-semibold"><Eye className="w-3 h-3" /> Client-visible</span>}
                    </p>
                    <p className="text-[13.5px] text-[#14110f] whitespace-pre-wrap">{n.body}</p>
                  </li>
                ))}
            </ul>
            <div className="mt-4">
              <textarea data-testid="note-body" value={noteBody} onChange={(e) => setNoteBody(e.target.value)} rows={3}
                        placeholder="Add a note…"
                        className="w-full px-3 py-2.5 rounded-md border border-neutral-200 text-[13.5px] focus:border-[#f5b300] outline-none" />
              <div className="mt-2 flex items-center justify-between">
                <label className="inline-flex items-center gap-2 text-[12.5px] text-neutral-700 cursor-pointer">
                  <input type="checkbox" checked={internalOnly} onChange={(e) => setInternalOnly(e.target.checked)} data-testid="note-internal" />
                  Internal only (hidden from member)
                </label>
                <button data-testid="note-send" onClick={addNote} className="inline-flex items-center gap-2 bg-[#14110f] text-white hover:bg-[#291d12] font-semibold px-4 py-2 rounded-md text-[12.5px]">
                  <Send className="w-3.5 h-3.5" /> Post note
                </button>
              </div>
            </div>
          </div>
        </div>

        <aside className="space-y-5">
          <div className="bg-white rounded-xl border border-neutral-200 p-5">
            <h3 className="text-[14px] font-bold text-[#14110f] mb-3 flex items-center gap-2"><History className="w-4 h-4 text-[#c89200]" /> Versions</h3>
            <ul className="space-y-2">
              {(doc.versions || []).slice().reverse().map((v) => (
                <li key={v.id} className="text-[12.5px] flex items-center justify-between">
                  <span className="text-neutral-700">v{v.version} · {bytesToHuman(v.size)}</span>
                  <span className="text-neutral-400">{fmtDateTime(v.uploaded_at)}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-white rounded-xl border border-neutral-200 p-5">
            <h3 className="text-[14px] font-bold text-[#14110f] mb-3 flex items-center gap-2"><Clock className="w-4 h-4 text-[#c89200]" /> Status history</h3>
            <ul className="space-y-2.5">
              {(doc.status_history || []).slice().reverse().map((s, i) => (
                <li key={i} className="text-[12.5px]">
                  <p className="text-[#14110f] font-semibold">{statusLabel(s.status)} <span className="text-neutral-400 text-[11px]">· {fmtDateTime(s.at)}</span></p>
                  {s.message && <p className="text-neutral-600">{s.message}</p>}
                  <p className="text-neutral-400 text-[11px]">by {s.changed_by_name}</p>
                </li>
              ))}
            </ul>
          </div>
        </aside>
      </div>
    </AdminLayout>
  );
}
